import {
    useSuspenseQuery,
    UseSuspenseQueryResult,
    UseMutationResult,
    useMutation,
    useQueryClient
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import {
    paginatedJobTitleResponseSchema,
    JobTitlesSearch,
    PaginatedJobTitleResponse,
    JobTitle
} from './jobTitles.types';
import { mutationResponseSchema, MutationResponse, parseMutationResponse } from '../shared.types';

const SEARCH_QUERY_KEY = 'JobTitleSearch';

const searchJobTitles = async (searchRequest: JobTitlesSearch): Promise<PaginatedJobTitleResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}jobtitle/search`, {
        json: searchRequest,
    }).json();

    // Parse & validate the response using Zod schema
    return paginatedJobTitleResponseSchema.parse(response);
};

export const useSearchJobTitles = (searchRequest: JobTitlesSearch): UseSuspenseQueryResult<PaginatedJobTitleResponse> => {
    return useSuspenseQuery<PaginatedJobTitleResponse>({
        queryKey: [SEARCH_QUERY_KEY, searchRequest],
        queryFn: () => searchJobTitles(searchRequest)
    });
}

const saveJobTitle = async (jobTitle: JobTitle): Promise<MutationResponse> => {

    let response;

    if (jobTitle.id && jobTitle.id > 0) {
        // Existing job title - update
        response = await createPacerApi().put(`${API_ROOT}jobtitle/update`, {
            json: jobTitle,
        }).json();
    } else {
        // New job title - create
        response = await createPacerApi().post(`${API_ROOT}jobtitle/create`, {
            json: jobTitle,
        }).json();
    }

    // Parse & validate the response using Zod schema
    return parseMutationResponse(response);
};

export const useSaveJobTitle = (callback?: (results: MutationResponse) => void): UseMutationResult<MutationResponse, Error, JobTitle> => {
    const queryClient = useQueryClient();

    return useMutation<MutationResponse, Error, JobTitle>({
        mutationFn: saveJobTitle,
        onSuccess: (mutationResponse) => {
            // Exec callback first (if passed) in case any state needs to be reset before invalidating the queries
            callback?.(mutationResponse);

            // Invalidate all queries starting with the job title search key prefix
            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
        },
        //throwOnError: true,
    });
}
